/**
 * Regions reference data — Zod schemas + DTOs.
 *
 * Three lookup tables imported from `info/regions.xlsx`:
 *   - Primarii (mayoralties) — keyed by numeric `cod`.
 *   - Entitati Silvice (forestry enterprises) — short codes.
 *   - Ocoale Silvice (forestry units) — children of entitati silvice.
 *
 * See ADR-013 (Phase A). Reference-only: no FKs from Parcel/PhotoRecord/User
 * in this batch.
 */

import { z } from 'zod';
import { PaginationMetaSchema } from './pagination.js';

// ----------------------------------------------------------------
// DTOs
// ----------------------------------------------------------------

export const PrimarieDtoSchema = z.object({
  id: z.string().uuid(),
  cod: z.string(),
  name: z.string(),
  raion: z.string(),
  rawComposite: z.string(),
  importBatchId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PrimarieDto = z.infer<typeof PrimarieDtoSchema>;

export const OcolSilvicDtoSchema = z.object({
  id: z.string().uuid(),
  cod: z.string(),
  entitateSilvicaId: z.string().uuid(),
  entitateSilvicaCod: z.string(),
  importBatchId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OcolSilvicDto = z.infer<typeof OcolSilvicDtoSchema>;

export const EntitateSilvicaDtoSchema = z.object({
  id: z.string().uuid(),
  cod: z.string(),
  name: z.string(),
  ocoale: z.array(OcolSilvicDtoSchema).default([]),
  importBatchId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EntitateSilvicaDto = z.infer<typeof EntitateSilvicaDtoSchema>;

// ----------------------------------------------------------------
// Update inputs (SUPER_ADMIN inline rename of reference rows).
// All `.strict()` — unknown keys are rejected. Every field is optional
// so a PATCH can touch a single column; the route applies only the
// provided fields. `rawComposite` (Primarie) is intentionally absent —
// it preserves the historical import value and is never edited.
// ----------------------------------------------------------------

export const PrimarieUpdateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    raion: z.string().trim().min(1).max(100).optional(),
  })
  .strict();
export type PrimarieUpdateInput = z.infer<typeof PrimarieUpdateInputSchema>;

// Create input — SUPER_ADMIN ad-hoc insert (without re-importing regions.xlsx).
// `rawComposite` is derived server-side to mirror the import's composite shape.
export const PrimarieCreateInputSchema = z
  .object({
    cod: z.string().trim().min(1).max(32),
    name: z.string().trim().min(1).max(200),
    raion: z.string().trim().min(1).max(100),
  })
  .strict();
export type PrimarieCreateInput = z.infer<typeof PrimarieCreateInputSchema>;

export const EntitateSilvicaUpdateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
  })
  .strict();
export type EntitateSilvicaUpdateInput = z.infer<
  typeof EntitateSilvicaUpdateInputSchema
>;

// OcolSilvic has no name column — `cod` is the only editable display field.
export const OcolSilvicUpdateInputSchema = z
  .object({
    cod: z.string().trim().min(1).max(64).optional(),
  })
  .strict();
export type OcolSilvicUpdateInput = z.infer<typeof OcolSilvicUpdateInputSchema>;

// Create input — SUPER_ADMIN ad-hoc insert. `entitateSilvicaId` is the parent
// FK; the route validates it exists before creating.
export const OcolSilvicCreateInputSchema = z
  .object({
    cod: z.string().trim().min(1).max(64),
    entitateSilvicaId: z.string().uuid(),
  })
  .strict();
export type OcolSilvicCreateInput = z.infer<typeof OcolSilvicCreateInputSchema>;

// ----------------------------------------------------------------
// Parsed-row payloads (preview)
// ----------------------------------------------------------------

export const ParsedPrimarieRowSchema = z.object({
  row: z.number().int(),
  cod: z.string(),
  rawComposite: z.string(),
  name: z.string(),
  raion: z.string(),
  valid: z.boolean(),
  classification: z.enum([
    'new',
    'duplicate-in-file',
    'already-imported',
    'name-changed',
  ]),
});
export type ParsedPrimarieRow = z.infer<typeof ParsedPrimarieRowSchema>;

export const ParsedEntitateSilvicaRowSchema = z.object({
  row: z.number().int(),
  cod: z.string(),
  name: z.string(),
  ocoale: z.array(z.string()),
  valid: z.boolean(),
  classification: z.enum([
    'new',
    'duplicate-in-file',
    'already-imported',
    'name-changed',
  ]),
});
export type ParsedEntitateSilvicaRow = z.infer<typeof ParsedEntitateSilvicaRowSchema>;

export const ParsedOcolSilvicRowSchema = z.object({
  row: z.number().int(),
  cod: z.string(),
  entitateSilvicaCod: z.string(),
  valid: z.boolean(),
  classification: z.enum([
    'new',
    'duplicate-in-file',
    'already-imported',
    'membership-changed',
  ]),
});
export type ParsedOcolSilvicRow = z.infer<typeof ParsedOcolSilvicRowSchema>;

export const RegionsDuplicateSchema = z.object({
  kind: z.enum(['duplicate-in-file', 'duplicate-in-db']),
  cod: z.string(),
  rawComposite: z.string().optional(),
  occursAt: z.array(z.number().int()),
  reason: z.enum([
    'duplicate-in-file',
    'already-imported',
    'name-changed',
    'membership-changed',
  ]),
});
export type RegionsDuplicate = z.infer<typeof RegionsDuplicateSchema>;

export const ParsedTreeSpeciesRowSchema = z.object({
  row: z.number().int(),
  cod: z.string(),
  valid: z.boolean(),
  classification: z.enum(['new', 'duplicate-in-file', 'already-imported']),
});
export type ParsedTreeSpeciesRow = z.infer<typeof ParsedTreeSpeciesRowSchema>;

export const ParsedWorkTypeRowSchema = z.object({
  row: z.number().int(),
  name: z.string(),
  code: z.string().nullable(),
  sortOrder: z.number().int(),
  valid: z.boolean(),
  classification: z.enum(['new', 'duplicate-in-file', 'already-imported']),
});
export type ParsedWorkTypeRow = z.infer<typeof ParsedWorkTypeRowSchema>;

export const RegionsRowErrorSchema = z.object({
  block: z.enum([
    'primarii',
    'entitatiSilvice',
    'ocoaleSilvice',
    'treeSpecies',
    'workTypes',
    'layout',
  ]),
  row: z.number().int(),
  code: z.string(),
  message: z.string(),
});
export type RegionsRowError = z.infer<typeof RegionsRowErrorSchema>;

// ----------------------------------------------------------------
// Parse response (preview envelope)
// ----------------------------------------------------------------

export const RegionsImportPreviewSchema = z.object({
  batchId: z.string().uuid(),
  importTarget: z.literal('region'),
  sheet: z.string(),
  terminationRow: z.number().int(),
  primarii: z.object({
    validCount: z.number().int(),
    invalidCount: z.number().int(),
    rows: z.array(ParsedPrimarieRowSchema),
    duplicates: z.array(RegionsDuplicateSchema),
  }),
  entitatiSilvice: z.object({
    validCount: z.number().int(),
    invalidCount: z.number().int(),
    rows: z.array(ParsedEntitateSilvicaRowSchema),
    duplicates: z.array(RegionsDuplicateSchema),
  }),
  ocoaleSilvice: z.object({
    validCount: z.number().int(),
    invalidCount: z.number().int(),
    rows: z.array(ParsedOcolSilvicRowSchema),
    duplicates: z.array(RegionsDuplicateSchema),
  }),
  treeSpecies: z.object({
    validCount: z.number().int(),
    invalidCount: z.number().int(),
    rows: z.array(ParsedTreeSpeciesRowSchema),
    duplicates: z.array(RegionsDuplicateSchema),
  }),
  workTypes: z.object({
    validCount: z.number().int(),
    invalidCount: z.number().int(),
    rows: z.array(ParsedWorkTypeRowSchema),
    duplicates: z.array(RegionsDuplicateSchema),
  }),
  oosBlocks: z.object({
    workTypes: z.boolean(),
    treeSpecies: z.boolean(),
    plantingScheme: z.boolean(),
  }),
  errors: z.array(RegionsRowErrorSchema),
});
export type RegionsImportPreview = z.infer<typeof RegionsImportPreviewSchema>;

// ----------------------------------------------------------------
// Commit input
// ----------------------------------------------------------------

export const RegionsImportCommitSchema = z
  .object({
    batchId: z.string().uuid(),
    mode: z.enum(['skip_invalid', 'all_or_nothing']).default('skip_invalid'),
    applyNameChanges: z.boolean().default(false),
  })
  .strict();
export type RegionsImportCommitInput = z.infer<typeof RegionsImportCommitSchema>;

// ----------------------------------------------------------------
// Commit response
// ----------------------------------------------------------------

export const RegionsBlockCountSchema = z.object({
  inserted: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
});
export type RegionsBlockCount = z.infer<typeof RegionsBlockCountSchema>;

export const RegionsImportCommitResponseSchema = z.object({
  batchId: z.string().uuid(),
  primarii: RegionsBlockCountSchema,
  entitatiSilvice: RegionsBlockCountSchema,
  ocoaleSilvice: RegionsBlockCountSchema,
  treeSpecies: RegionsBlockCountSchema,
  workTypes: RegionsBlockCountSchema,
  errors: z.array(RegionsRowErrorSchema),
  durationMs: z.number().int(),
});
export type RegionsImportCommitResponse = z.infer<
  typeof RegionsImportCommitResponseSchema
>;

// ----------------------------------------------------------------
// List responses (read endpoints)
// ----------------------------------------------------------------

export const PrimariiListResponseSchema = z.object({
  items: z.array(PrimarieDtoSchema),
  pagination: PaginationMetaSchema,
});
export type PrimariiListResponse = z.infer<typeof PrimariiListResponseSchema>;

export const OcoaleListResponseSchema = z.object({
  items: z.array(OcolSilvicDtoSchema),
  pagination: PaginationMetaSchema,
});
export type OcoaleListResponse = z.infer<typeof OcoaleListResponseSchema>;

export const EntitatiSilviceListResponseSchema = z.object({
  items: z.array(EntitateSilvicaDtoSchema),
  total: z.number().int(),
});
export type EntitatiSilviceListResponse = z.infer<
  typeof EntitatiSilviceListResponseSchema
>;

// ----------------------------------------------------------------
// Species + work types (2026-06-08) — reference DTOs + list responses.
// Seeded from info/regions2.xlsx (sheet "date").
// ----------------------------------------------------------------

export const TreeSpeciesDtoSchema = z.object({
  id: z.string().uuid(),
  cod: z.string(),
  name: z.string().nullable(),
  importBatchId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TreeSpeciesDto = z.infer<typeof TreeSpeciesDtoSchema>;

export const WorkTypeDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  code: z.string().nullable(),
  sortOrder: z.number().int().nullable(),
  importBatchId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WorkTypeDto = z.infer<typeof WorkTypeDtoSchema>;

// TreeSpecies.name is nullable — `.nullable().optional()` lets a PATCH
// either set a label or clear it back to null.
export const TreeSpeciesUpdateInputSchema = z
  .object({
    name: z.string().trim().max(200).nullable().optional(),
  })
  .strict();
export type TreeSpeciesUpdateInput = z.infer<
  typeof TreeSpeciesUpdateInputSchema
>;

// Create input — SUPER_ADMIN ad-hoc insert. `cod` is the unique key; `name`
// is the optional (nullable) display label.
export const TreeSpeciesCreateInputSchema = z
  .object({
    cod: z.string().trim().min(1).max(16),
    name: z.string().trim().max(200).nullable().optional(),
  })
  .strict();
export type TreeSpeciesCreateInput = z.infer<
  typeof TreeSpeciesCreateInputSchema
>;

// WorkType.name IS the unique key; renaming it is a key change (the route
// pre-checks for a collision). `code` is a nullable secondary field.
export const WorkTypeUpdateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(400).optional(),
    code: z.string().trim().max(32).nullable().optional(),
  })
  .strict();
export type WorkTypeUpdateInput = z.infer<typeof WorkTypeUpdateInputSchema>;

// Create input — SUPER_ADMIN ad-hoc insert. `name` IS the unique key; `code`
// and `sortOrder` are nullable secondary fields.
export const WorkTypeCreateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(400),
    code: z.string().trim().max(32).nullable().optional(),
    sortOrder: z.number().int().nullable().optional(),
  })
  .strict();
export type WorkTypeCreateInput = z.infer<typeof WorkTypeCreateInputSchema>;

export const TreeSpeciesListResponseSchema = z.object({
  items: z.array(TreeSpeciesDtoSchema),
});
export type TreeSpeciesListResponse = z.infer<
  typeof TreeSpeciesListResponseSchema
>;

export const WorkTypeListResponseSchema = z.object({
  items: z.array(WorkTypeDtoSchema),
});
export type WorkTypeListResponse = z.infer<typeof WorkTypeListResponseSchema>;

// ----------------------------------------------------------------
// Project types (2026-06-09) — editable reference table replacing the old
// hardcoded `PhotoProject` enum. `name` IS the unique key referenced by
// `photo_record.project` (reference-only, no FK). Seeded with 6 canonical
// rows; SUPER_ADMIN can add/rename via the admin endpoints.
// ----------------------------------------------------------------

export const ProjectTypeDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sortOrder: z.number().int().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProjectTypeDto = z.infer<typeof ProjectTypeDtoSchema>;

// `name` IS the unique key; renaming it is a key change (the route pre-checks
// for a collision → 409). `sortOrder` is a nullable secondary field.
export const ProjectTypeUpdateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    sortOrder: z.number().int().nullable().optional(),
  })
  .strict();
export type ProjectTypeUpdateInput = z.infer<
  typeof ProjectTypeUpdateInputSchema
>;

// Create input — SUPER_ADMIN ad-hoc insert. `name` IS the unique key
// (pre-checked for a collision → 409); `sortOrder` is a nullable secondary
// field.
export const ProjectTypeCreateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    sortOrder: z.number().int().nullable().optional(),
  })
  .strict();
export type ProjectTypeCreateInput = z.infer<
  typeof ProjectTypeCreateInputSchema
>;

export const ProjectTypeListResponseSchema = z.object({
  items: z.array(ProjectTypeDtoSchema),
});
export type ProjectTypeListResponse = z.infer<
  typeof ProjectTypeListResponseSchema
>;
